// Chakra imports
import { Box, ChakraProvider, Portal, useDisclosure, useColorMode } from '@chakra-ui/react';
import Configurator from '../components/Configurator/Configurator';
import NotificationToastListener from '../components/NotificationToastListener';
// Layout components
import AdminNavbar from '../components/Navbars/AdminNavbar';
import Sidebar from '../components/Sidebar';
import React, { useState, useEffect } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import routes from '../routes';
import '@fontsource/urbanist/300.css';
import '@fontsource/urbanist/400.css';
import '@fontsource/urbanist/500.css';
import '@fontsource/urbanist/600.css';
import '@fontsource/urbanist/700.css';
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-sans/700.css';
// Custom Chakra theme
import theme from '../theme/theme';
// Custom components
import MainPanel from '../components/Layout/MainPanel';
import PanelContainer from '../components/Layout/PanelContainer';
import AppPanelRouteHost from '../components/Layout/AppPanelRouteHost';
import PanelContent from '../components/Layout/PanelContent';
import { RouteConfigArray } from '../types/routes';
import { SIDEBAR_WIDTH_TRANSITION } from '../constants/transitions';
import { DisplayTimezoneProvider } from '../contexts/DisplayTimezoneContext';
import { DisplayCurrencyProvider } from '../contexts/DisplayCurrencyContext';
import { ActiveBrokerAccountProvider } from '../hooks/useActiveBrokerAccount';
import { InstrumentSetupProvider } from '../contexts/InstrumentSetupContext';
import { InstrumentSetupBanner } from '../components/Instruments/InstrumentSetupBanner';
import { BillingProvider } from '../contexts/BillingContext';
import { CurrentUserProvider } from '../contexts/CurrentUserContext';
import BillingGate from '../components/Billing/BillingGate';
import ServiceUnavailableGate from '../components/Billing/ServiceUnavailableGate';
import BillingPaywallEnforcer from '../components/Billing/BillingPaywallEnforcer';
import { isBillingStripeReturnFullscreenPath } from '../utils/billingAllowlist';
import { getBreadcrumbTrail } from '../utils/breadcrumbPageTitle';
import { resolveDocumentTitle } from '../utils/documentTitle';
import DashboardHome from '../views/Dashboard/Dashboard';
import { DashboardShell } from '../components/DashboardShell';
import { TooltipProvider } from '@/components/ui/tooltip';
import { isShadcnSidebarEnabled } from '../config/features';
import { useTheme } from 'next-themes';

function ChakraThemeSync() {
	const { resolvedTheme } = useTheme();
	const { colorMode, setColorMode } = useColorMode();

	React.useEffect(() => {
		if (!resolvedTheme) return;
		if (colorMode !== resolvedTheme) {
			setColorMode(resolvedTheme);
		}
	}, [resolvedTheme, colorMode, setColorMode]);

	return null;
}

interface DashboardProps {
  [key: string]: any;
}

export default function Dashboard(props: DashboardProps) {
	const { ...rest } = props;
	const location = useLocation();
	const billingStripeFullscreen = isBillingStripeReturnFullscreenPath(location.pathname);
	
	// Scroll to top on route change
	useEffect(() => {
		window.scrollTo(0, 0);
	}, [location.pathname]);
	
	// states and functions
	const [ sidebarVariant, setSidebarVariant ] = useState<string>('opaque');
	const [ fixed, setFixed ] = useState<boolean>(false);
	
	// functions for changing the states from components
	const getRoute = (): boolean => {
		return window.location.pathname !== '/full-screen-maps';
	};
	
	const getActiveRoute = (routes: RouteConfigArray): string => {
		if (!routes || !Array.isArray(routes) || routes.length === 0) {
			return 'Default Brand Text';
		}
		let activeRoute = 'Default Brand Text';
		for (let i = 0; i < routes.length; i++) {
			const route = routes[i];
			if (route.collapse && route.views) {
				const collapseActiveRoute = getActiveRoute(route.views);
				if (collapseActiveRoute !== activeRoute) {
					return collapseActiveRoute;
				}
			} else if (route.category && route.views) {
				const categoryActiveRoute = getActiveRoute(route.views);
				if (categoryActiveRoute !== activeRoute) {
					return categoryActiveRoute;
				}
			} else {
				if (route.path) {
					const fullPath = `${route.layout || ""}${route.path}`;
					if (window.location.href.indexOf(fullPath) === -1) continue;
					return route.name;
				}
			}
		}
		return activeRoute;
	};

	// This changes navbar state(fixed or not)
	const getActiveNavbar = (routes: RouteConfigArray): boolean => {
		if (!routes || !Array.isArray(routes) || routes.length === 0) {
			return false;
		}
		let activeNavbar = false;
		for (let i = 0; i < routes.length; i++) {
			const route = routes[i];
			if (route.category && route.views) {
				const categoryActiveNavbar = getActiveNavbar(route.views);
				if (categoryActiveNavbar !== activeNavbar) {
					return categoryActiveNavbar;
				}
			} else {
				if (route.path) {
					const fullPath = `${route.layout || ""}${route.path}`;
					if (window.location.href.indexOf(fullPath) === -1) continue;
					if (route.secondaryNavbar) {
						return route.secondaryNavbar;
					}
				}
			}
		}
		return activeNavbar;
	};
	
	const getRoutes = (routes: RouteConfigArray): JSX.Element[] => {
		if (!routes || !Array.isArray(routes) || routes.length === 0) {
			return [];
		}
		const routeList: JSX.Element[] = [];
		routes.forEach((prop, key) => {
			if (prop.collapse && prop.views) {
				routeList.push(...getRoutes(prop.views));
			} else if (prop.category === 'account' && prop.views) {
				routeList.push(...getRoutes(prop.views));
			} else if (prop.layout !== '/auth' && prop.layout !== '/rtl' && prop.path && prop.component) {
				// Use exact prop for routes without parameters to prevent matching more specific routes
				// Also use exact for course overview and specific course learn route
				const isExact =
					!prop.path.includes(':') ||
					prop.path === '/university/course/:courseId' ||
					prop.path === '/university/course/:courseId/module/:moduleId/lesson/:lessonId';
				// Use strict matching for course routes to ensure correct matching
				const isStrict = prop.path.includes('/university/course/');
				routeList.push(
					<Route 
						exact={isExact}
						strict={isStrict}
						path={(prop.layout || "") + prop.path} 
						component={prop.component} 
						key={key} 
					/>
				);
			}
		});
		return routeList;
	};
	
	const { isOpen, onOpen, onClose } = useDisclosure();
	document.documentElement.dir = 'ltr';
	
	// Sidebar collapse state
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
		const saved = localStorage.getItem('sidebarCollapsed');
		return saved ? JSON.parse(saved) : false;
	});

	const toggleSidebar = () => {
		const newState = !isSidebarCollapsed;
		setIsSidebarCollapsed(newState);
		localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
	};

	const useShadcnSidebar = isShadcnSidebarEnabled();
	const navRoutes = routes as RouteConfigArray;

	useEffect(() => {
		if (useShadcnSidebar) return;
		const trail = getBreadcrumbTrail(navRoutes, location.pathname, location.search);
		document.title = resolveDocumentTitle(location.pathname, trail);
	}, [useShadcnSidebar, location.pathname, location.search, navRoutes]);

	const handleShadcnSidebarOpenChange = (open: boolean) => {
		const collapsed = !open;
		setIsSidebarCollapsed(collapsed);
		localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
	};

	const mainPanelInner = (
		<>
			{!useShadcnSidebar ? (
				<Portal>
					<AdminNavbar
						onOpen={onOpen}
						logoText={'PURITY UI DASHBOARD'}
						brandText={getActiveRoute(navRoutes)}
						secondary={getActiveNavbar(navRoutes)}
						fixed={fixed}
						display={{ base: 'flex', xl: 'none' }}
						{...rest}
					/>
				</Portal>
			) : null}
			{getRoute() ? (
				<PanelContent>
					<PanelContainer>
						<AppPanelRouteHost>
							<BillingGate>
								<ServiceUnavailableGate>
								<Switch>
									{getRoutes(navRoutes)}
									<Redirect from='/admin' to='/dashboard' />
								</Switch>
								</ServiceUnavailableGate>
							</BillingGate>
						</AppPanelRouteHost>
					</PanelContainer>
				</PanelContent>
			) : null}
			<Configurator
				secondary={getActiveNavbar(navRoutes)}
				isOpen={isOpen}
				onClose={onClose}
				isChecked={fixed}
				onSwitch={(value: boolean) => {
					setFixed(value);
				}}
				onOpaque={() => setSidebarVariant('opaque')}
				onTransparent={() => setSidebarVariant('transparent')}
				{...({} as any)}
			/>
		</>
	);
	
	// Chakra Color Mode
	return (
		<ChakraProvider theme={theme as any} resetCss={false} {...({} as any)}>
			<ChakraThemeSync />
			<Box data-app-route-surface minH="100vh" w="100%">
			<DisplayTimezoneProvider>
				<DisplayCurrencyProvider>
				<ActiveBrokerAccountProvider>
					<InstrumentSetupProvider>
					<CurrentUserProvider>
					<BillingProvider>
						<BillingPaywallEnforcer>
						<NotificationToastListener />
						{billingStripeFullscreen ? (
							// Stripe return skips DashboardShell; still provide TooltipProvider for
							// dashboard blur preview (e.g. MetricHelpIconTooltip on home cards).
							<TooltipProvider delayDuration={0}>
							<Box position="relative" minH="100vh" w="100%" overflow="hidden">
								<Box
									position="fixed"
									inset={0}
									zIndex={0}
									overflow="hidden"
									pointerEvents="none"
									aria-hidden
								>
									<Box filter="blur(8px)" opacity={0.94} h="100%" overflow="hidden">
										<Sidebar
											routes={routes as RouteConfigArray}
											logoText={'PURITY UI DASHBOARD'}
											display='none'
											sidebarVariant={sidebarVariant}
											isCollapsed={isSidebarCollapsed}
											onToggleSidebar={toggleSidebar}
											onOpen={onOpen}
											{...rest}
										/>
										<MainPanel
											w={{
												base: '100%',
												xl: isSidebarCollapsed ? 'calc(100% - 95px)' : 'calc(100% - 275px)'
											}}
											transition={SIDEBAR_WIDTH_TRANSITION}
											style={{
												willChange: 'width',
												backfaceVisibility: 'hidden',
												transform: 'translateZ(0)',
											}}
										>
											<Portal>
												<AdminNavbar
													onOpen={onOpen}
													logoText={'PURITY UI DASHBOARD'}
													brandText="Dashboard"
													secondary={getActiveNavbar(routes as RouteConfigArray)}
													fixed={fixed}
													display={{ base: 'flex', xl: 'none' }}
													{...rest}
												/>
											</Portal>
											{getRoute() ? (
												<PanelContent>
													<PanelContainer>
														<AppPanelRouteHost publishRouteReady={false}>
															<DashboardHome />
														</AppPanelRouteHost>
													</PanelContainer>
												</PanelContent>
											) : null}
										</MainPanel>
									</Box>
								</Box>
								<Box position="relative" zIndex={1} minH="100vh">
									<AppPanelRouteHost>
										<BillingGate>
											<ServiceUnavailableGate>
											<InstrumentSetupBanner />
											<Switch>
												{getRoutes(routes as RouteConfigArray)}
												<Redirect from='/admin' to='/dashboard' />
											</Switch>
											</ServiceUnavailableGate>
										</BillingGate>
									</AppPanelRouteHost>
								</Box>
							</Box>
							</TooltipProvider>
						) : useShadcnSidebar ? (
							<DashboardShell
								sidebarOpen={!isSidebarCollapsed}
								onSidebarOpenChange={handleShadcnSidebarOpenChange}
							>
								<MainPanel w="100%" className="flex min-h-0 min-w-0 flex-1 flex-col bg-content-canvas">
									{mainPanelInner}
								</MainPanel>
							</DashboardShell>
						) : (
							<>
								<Sidebar
									routes={navRoutes}
									logoText={'PURITY UI DASHBOARD'}
									display='none'
									sidebarVariant={sidebarVariant}
									isCollapsed={isSidebarCollapsed}
									onToggleSidebar={toggleSidebar}
									onOpen={onOpen}
									{...rest}
								/>
								<MainPanel
									w={{
										base: '100%',
										xl: isSidebarCollapsed ? 'calc(100% - 95px)' : 'calc(100% - 275px)'
									}}
									transition={SIDEBAR_WIDTH_TRANSITION}
									style={{
										willChange: 'width',
										backfaceVisibility: 'hidden',
										transform: 'translateZ(0)',
									}}
								>
									{mainPanelInner}
								</MainPanel>
							</>
						)}
						</BillingPaywallEnforcer>
					</BillingProvider>
					</CurrentUserProvider>
					</InstrumentSetupProvider>
				</ActiveBrokerAccountProvider>
				</DisplayCurrencyProvider>
			</DisplayTimezoneProvider>
			</Box>
		</ChakraProvider>
	);
}

